import { useState, useRef, useEffect } from "react";
import type { Period } from "@yarnalytics/shared";
import { MANUAL_SYNC_COOLDOWN } from "@yarnalytics/shared";
import { ChevronDown, RefreshCw, Loader2 } from "lucide-react";
import { useStore } from "@/lib/store";
import { useInsightsSummary, useInsightsDaily, useSyncTrigger, useSyncStatus } from "@/lib/hooks";
import { PeriodSelector } from "@/components/PeriodSelector";
import { MetricCard } from "@/components/MetricCard";
import { DailyChart } from "@/components/DailyChart";

const LOCKED_PERIODS: Period[] = ["30d", "90d"];

const metricOptions = [
  { value: "views", label: "Views" },
  { value: "likes", label: "Likes" },
  { value: "replies", label: "Replies" },
  { value: "reposts", label: "Reposts" },
  { value: "quotes", label: "Quotes" },
  { value: "new_followers", label: "New Followers" },
];

export function InsightsPage() {
  const [period, setPeriod] = useState<Period>("7d");
  const [primaryMetric, setPrimaryMetric] = useState("views");
  const [secondaryMetric, setSecondaryMetric] = useState<string | null>(null);
  const activeAccountId = useStore((s) => s.activeAccountId);
  const activeAccount = useStore((s) => s.activeAccount());
  const isPro = useStore((s) => s.isPro());
  const disabledPeriods = isPro ? [] : LOCKED_PERIODS;

  const syncTrigger = useSyncTrigger();
  const { isAwaitingFirstSync } = useSyncStatus(
    activeAccountId,
    activeAccount?.lastSyncedAt ?? null
  );

  // Cooldown: disable sync button if last sync was < 5 min ago
  const lastSyncedAt = activeAccount?.lastSyncedAt ?? null;
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!isPro || !lastSyncedAt) return;
    const interval = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(interval);
  }, [isPro, lastSyncedAt]);
  const isCooldown = lastSyncedAt !== null && now - lastSyncedAt < MANUAL_SYNC_COOLDOWN;
  const isSyncing = syncTrigger.isPending;

  useEffect(() => {
    if (!isPro && disabledPeriods.includes(period)) {
      setPeriod("7d");
    }
  }, [disabledPeriods, isPro, period]);

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useInsightsSummary(activeAccountId, period);
  const {
    data: primaryDaily,
    isLoading: primaryLoading,
    isError: dailyError,
  } = useInsightsDaily(activeAccountId, period, primaryMetric);
  const { data: secondaryDaily } = useInsightsDaily(
    activeAccountId,
    period,
    secondaryMetric || "" // hook disabled when empty via enabled guard below
  );

  const chartLoading = primaryLoading;

  // Filter secondary options to exclude current primary
  const secondaryOptions = metricOptions.filter(
    (m) => m.value !== primaryMetric
  );

  // Reset secondary if it matches newly-selected primary
  function handlePrimaryChange(value: string) {
    if (secondaryMetric === value) {
      setSecondaryMetric(null);
    }
    setPrimaryMetric(value);
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Overview</h1>
        <div className="flex items-center gap-2">
          <PeriodSelector
            value={period}
            onChange={setPeriod}
            disabledValues={disabledPeriods}
          />
          {isPro && (
            <button
              onClick={() => activeAccountId && syncTrigger.mutate(activeAccountId)}
              disabled={isSyncing || isCooldown || !activeAccountId}
              title={isCooldown ? "Please wait before syncing again" : "Refresh data"}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? "Syncing..." : "Refresh"}
            </button>
          )}
        </div>
      </div>

      {/* Metric Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl bg-surface" />
          ))}
        </div>
      ) : summaryError ? (
        <div className="rounded-xl border border-danger/20 bg-red-50 p-4 text-sm text-danger">
          We couldn’t load your overview metrics. Please try again shortly.
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <MetricCard label="Followers" value={summary.followersCount} format="compact" />
          <MetricCard label="New Followers" value={summary.newFollowers} />
          <MetricCard label="Views" value={summary.views} format="compact" />
          <MetricCard label="Likes" value={summary.likes} format="compact" />
          <MetricCard label="Replies" value={summary.replies} />
          <MetricCard label="Reposts" value={summary.reposts} />
          <MetricCard label="Quotes" value={summary.quotes} />
        </div>
      ) : isAwaitingFirstSync ? (
        <div className="rounded-xl border border-border bg-surface p-8 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-neutral-400 mb-3" />
          <p className="text-sm font-medium text-neutral-600">Syncing your data...</p>
          <p className="text-xs text-neutral-400 mt-1">This usually takes less than a minute.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-surface p-8 text-center text-sm text-neutral-400">
          No data yet. Data will appear after the first sync.
        </div>
      )}

      {/* Daily Chart */}
      <div className="mt-8 rounded-xl border border-border bg-white p-5">
        {/* Chart header: primary pills + secondary dropdown */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h2 className="text-lg font-semibold">Daily Trend</h2>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Primary metric pills */}
            <div className="flex gap-1">
              {metricOptions.map((m) => (
                <button
                  key={m.value}
                  onClick={() => handlePrimaryChange(m.value)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    primaryMetric === m.value
                      ? "bg-black text-white"
                      : "text-neutral-400 hover:text-black"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Separator */}
            <div className="hidden sm:block h-5 w-px bg-border" />

            {/* Secondary metric dropdown */}
            <SecondarySelector
              value={secondaryMetric}
              options={secondaryOptions}
              onChange={setSecondaryMetric}
            />
          </div>
        </div>

        {chartLoading ? (
          <div className="h-80 animate-pulse rounded-lg bg-surface" />
        ) : dailyError ? (
          <div className="rounded-lg border border-danger/20 bg-red-50 p-4 text-sm text-danger">
            We couldn’t load the daily trend. Please try again shortly.
          </div>
        ) : primaryDaily && primaryDaily.metrics.length > 0 ? (
          <DailyChart
            primaryData={primaryDaily.metrics}
            primaryMetric={primaryMetric}
            secondaryData={
              secondaryMetric && secondaryDaily
                ? secondaryDaily.metrics
                : undefined
            }
            secondaryMetric={secondaryMetric}
          />
        ) : (
          <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-neutral-400">
            No daily data yet.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Secondary Stat Dropdown ──

function SecondarySelector({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: { value: string; label: string }[];
  onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectedLabel = value
    ? options.find((o) => o.value === value)?.label || value
    : null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
          value
            ? "border-neutral-300 bg-neutral-100 text-black"
            : "border-border text-neutral-400 hover:border-neutral-300 hover:text-black"
        }`}
      >
        <span className="text-neutral-400">+</span>
        <span>{selectedLabel || "Compare"}</span>
        <ChevronDown className="h-3 w-3 text-neutral-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 min-w-[160px] rounded-lg border border-border bg-white py-1 shadow-lg">
          {/* None option to clear */}
          {value && (
            <button
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="flex w-full items-center px-3 py-2 text-left text-xs text-neutral-400 hover:bg-surface-hover transition-colors"
            >
              Remove comparison
            </button>
          )}
          {value && <div className="my-1 border-t border-border" />}

          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-surface-hover transition-colors ${
                value === opt.value
                  ? "font-semibold text-black"
                  : "text-neutral-600"
              }`}
            >
              {/* Color indicator */}
              <div
                className={`h-2 w-2 rounded-sm ${
                  value === opt.value ? "bg-neutral-400" : "bg-neutral-200"
                }`}
              />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
